import { ChecklistEntry, ChecklistCategory } from '../types';

// Gemeinsame Checklisten-Logik für Desktop (ChecklistView) und Mobile.
// Master-Vorlage + gespeicherte Einträge werden zu einer Ansicht gemerged;
// Vorlagen-Punkte ohne gespeicherten Stand sind "virtuell" (id 'virtual-*')
// und werden erst bei der ersten Bearbeitung mit echter ID materialisiert.
// createId wird injiziert (statt crypto.randomUUID direkt) — testbar ohne jsdom.

export const MASTER_CHECKLIST: Omit<ChecklistEntry, 'isRelevant' | 'lastChecked' | 'status' | 'comment' | 'id'>[] = [
  { text: 'Vorankündigung liegt aktuell vor und hängt aus', category: 'Dokumente' },
  { text: 'SiGe-Plan liegt vor und ist aktuell', category: 'Dokumente' },
  { text: 'Baustellenordnung hängt aus', category: 'Dokumente' },
  { text: 'Unterweisungsnachweise der Firmen liegen vor', category: 'Dokumente' },
  { text: 'Gefährdungsbeurteilungen der Firmen liegen vor', category: 'Dokumente' },
  { text: 'Ersthelfer-Liste aktuell', category: 'Dokumente' },
  { text: 'Bauzaun geschlossen und standsicher', category: 'Baustelleneinrichtung' },
  { text: 'Baustellenschilderung / Warnhinweise vorhanden', category: 'Baustelleneinrichtung' },
  { text: 'Verkehrswege (Personen/Fahrzeuge) getrennt & frei', category: 'Baustelleneinrichtung' },
  { text: 'Erste-Hilfe-Material vorhanden & aufgefüllt', category: 'Baustelleneinrichtung' },
  { text: 'Pausenräume / Sanitäranlagen in Ordnung', category: 'Baustelleneinrichtung' },
  { text: 'Brandschutz (Feuerlöscher) geprüft vorhanden', category: 'Baustelleneinrichtung' },
  { text: 'Beleuchtung der Verkehrswege ausreichend', category: 'Baustelleneinrichtung' },
  { text: 'Persönliche Schutzausrüstung (PSA) wird getragen', category: 'Allgemein' },
  { text: 'Elektrische Anlagen geprüft (Prüfplaketten)', category: 'Allgemein' },
  { text: 'Leitern und Tritte geprüft und mängelfrei', category: 'Allgemein' },
  { text: 'Umgang mit Gefahrstoffen (Lagerung/Kennzeichnung)', category: 'Allgemein' },
  { text: 'Absturzsicherungen (Seitenschutz) vollständig', category: 'Hochbau' },
  { text: 'Gerüste: Freigabenschein vorhanden & aktuell', category: 'Hochbau' },
  { text: 'Gerüste: Verankerung & Belag in Ordnung', category: 'Hochbau' },
  { text: 'Bodenöffnungen abgedeckt / gesichert', category: 'Hochbau' },
  { text: 'Kranbetrieb: Standsicherheit & Prüfbescheinigung', category: 'Hochbau' },
  { text: 'Baugrubenböschungen verbaute / geböscht', category: 'Tiefbau' },
  { text: 'Arbeitsraumbreite (> 0,50m) eingehalten', category: 'Tiefbau' },
  { text: 'Absturzsicherung an Baugrubenrändern', category: 'Tiefbau' },
  { text: 'Leitungspläne vorhanden / eingewiesen', category: 'Tiefbau' },
  { text: 'Zugänge zu Baugruben (Treppentürme/Leitern)', category: 'Tiefbau' },
];

export const CHECKLIST_CATEGORIES: ChecklistCategory[] = ['Allgemein', 'Dokumente', 'Baustelleneinrichtung', 'Hochbau', 'Tiefbau', 'Brückenbau', 'Industrie', 'Abbruch', 'Benutzerdefiniert', 'SiGe-Plan'];

export const isVirtualId = (id: string): boolean => id.startsWith('virtual-');

const normalizeSectionId = (activeSectionId?: string | null): string | null =>
    (activeSectionId && activeSectionId !== 'all') ? activeSectionId : null;

export const mergeChecklistEntries = (entries: ChecklistEntry[], activeSectionId?: string | null): ChecklistEntry[] => {
    const normalizedSectionId = normalizeSectionId(activeSectionId);
    const currentSavedEntries = entries.filter(e => (e.sectionId || null) === normalizedSectionId);

    const masterItems: ChecklistEntry[] = MASTER_CHECKLIST.map((m, index) => {
        const saved = currentSavedEntries.find(e => e.text === m.text && e.category === m.category);
        if (saved) return saved;
        return {
            ...m,
            id: `virtual-${index}-${m.category.replace(/\s+/g, '')}`,
            isRelevant: false,
            lastChecked: null,
            status: null,
            comment: '',
            sectionId: normalizedSectionId || undefined,
            isCustom: false,
        };
    });

    const customEntries = currentSavedEntries.filter(e =>
        !MASTER_CHECKLIST.some(m => m.text === e.text && m.category === e.category)
    );

    return [...masterItems, ...customEntries];
};

export const updateChecklistEntry = (
    entries: ChecklistEntry[],
    viewEntry: ChecklistEntry,
    updates: Partial<ChecklistEntry>,
    activeSectionId: string | null | undefined,
    createId: () => string,
): ChecklistEntry[] => {
    const sectionIdToSave = normalizeSectionId(activeSectionId) || undefined;

    if (isVirtualId(viewEntry.id)) {
        const newEntry: ChecklistEntry = {
            id: createId(),
            text: viewEntry.text,
            category: viewEntry.category,
            isRelevant: updates.isRelevant !== undefined ? updates.isRelevant : viewEntry.isRelevant,
            lastChecked: updates.lastChecked !== undefined ? updates.lastChecked : viewEntry.lastChecked,
            status: updates.status !== undefined ? updates.status : viewEntry.status,
            comment: updates.comment !== undefined ? updates.comment : viewEntry.comment,
            sectionId: sectionIdToSave,
            isCustom: false,
        };
        return [...entries, newEntry];
    }
    return entries.map(e => e.id === viewEntry.id ? { ...e, ...updates } : e);
};

export const setAllChecklistRelevance = (
    entries: ChecklistEntry[],
    mergedList: ChecklistEntry[],
    activeCategories: ChecklistCategory[],
    relevant: boolean,
    activeSectionId: string | null | undefined,
    createId: () => string,
): ChecklistEntry[] => {
    const sectionIdToSave = normalizeSectionId(activeSectionId) || undefined;
    let newEntries = [...entries];

    mergedList.forEach(item => {
        if (!activeCategories.includes(item.category)) return;
        if (isVirtualId(item.id)) {
            if (relevant) {
                newEntries.push({
                    id: createId(),
                    text: item.text,
                    category: item.category,
                    isRelevant: true,
                    lastChecked: null,
                    status: null,
                    comment: '',
                    sectionId: sectionIdToSave,
                    isCustom: false,
                });
            }
        } else {
            newEntries = newEntries.map(e => e.id === item.id ? { ...e, isRelevant: relevant } : e);
        }
    });
    return newEntries;
};
